import { Injectable } from '@angular/core';
import { Classroom } from '@models/classroom';
import { IClassroomContext } from '@models/classroom-context';
import { IProductLine } from '@models/product-line';
import { School } from '@models/school';
import { IUser } from '@models/user';
import { AccessDeniedReason, accessDeniedReasonSort } from '@shared/enums/access-denied-reason';
import { ParentRoutingAccess } from '@shared/enums/parent-routing-access';
import { RoleType } from '@shared/enums/role-type';
import { TeacherLicenseAccess } from '@shared/enums/teacher-license-acccess';
import { Helpers } from '@shared/helpers';

/**
 * Handles logic for allowing or blocking access to places throughout the application.
 */
@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  teacherAllSchoolsLicenseAccessCheck(teacher: IUser): { reason: AccessDeniedReason, school: School } | null {
    if (!teacher.isViewingAsRole(RoleType.Teacher)) {
      return null;
    }
    const reasons = teacher.schools
      // check each school for access
      .map((school) => {
        const reason = this.teacherSchoolLicenseAccessCheck(school);
        return { reason, school };
      })
      // sort the reasons by how far they are in the process
      .sort((a, b) => accessDeniedReasonSort(a.reason, b.reason));
    // the reason on the end of the list should be the closest to granting access
    const result = reasons.pop();
    return result?.reason ? result : null;
  }

  teacherSchoolLicenseAccessCheck(school: School): AccessDeniedReason | null {
    if (!this.hasSchoolIdSet(school)) {
      return AccessDeniedReason.MissingSchoolID;
    }
    if (!this.hasLicenseStartEndDates(school)) {
      return AccessDeniedReason.SchoolLicensesStartEndDatesNotSet;
    }
    if (this.hasFutureLicenses(school)) {
      return AccessDeniedReason.SchoolLicensesStartInFuture;
    }
    if (this.belongsToDistrict(school) && !this.hasLicensesDistributed(school)) {
      return AccessDeniedReason.SchoolLicensesNotDistributed;
    }
    // passes check, no deny reason given
    return null;
  }

  /**
   * Checks if a school has valid licenses.
   * Order of checks is important, will short-circuit if certain requirements are met
   * @param {School} school The school whose licenses is being checked.
   * @returns {AccessDeniedReason | null} The reason for access denial, or null if access is granted.
   */
  schoolAdminSchoolLicenseAccessCheck(school: School): AccessDeniedReason | null {
    // TODO Decisions still need to be made about the flow logic for school admins. Once that logic is final,
    //  the scenarios should leverage existing methods / be represented by a method that ca be reused.

    // Scenario 1
    if (school && !school.hasAvailableLicenses && !school.hasFutureActivation) {
      return AccessDeniedReason.SchoolLicensesNotAvailable;
    }
    return null;
  }

  private belongsToDistrict(school: School): boolean {
    return !!school && !!school.districtId;
  }

  private hasSchoolIdSet(school: School): boolean {
    return school && school.abbreviatedSchoolId && school.abbreviatedSchoolId.length > 0;
  }

  private hasLicensesDistributed(school: School): boolean {
    return school && school.hasLicensesDesignated;
  }

  private hasLicenseStartEndDates(school: School): boolean {
    return school && school.schoolYearDates
      && !!school.schoolYearDates.schoolYear
      && !!school.schoolYearDates.schoolYearStartDate
      && !!school.schoolYearDates.schoolYearEndDate;
  }

  private hasFutureLicenses(school: School): boolean {
    return school && school.hasFutureActivation;
  }

  /** Checks if parents have at least 1 student with at least 1 classroom. */
  parentChildrenRoutingCheck(children: IUser[]): ParentRoutingAccess {
    if (!children?.length) return ParentRoutingAccess.RouteToNoStudents;
    if (!children[0].classrooms?.length) return ParentRoutingAccess.RouteToNoClassroomForStudent;
    return ParentRoutingAccess.RouteToStudentClassroom;
  }

  /** Checks if parents have a license to access a specific classroom. */
  parentClassroomLicenseAccessCheck(context: IClassroomContext): ParentRoutingAccess | null {
    if (!context?.classroom || context.classroom.assignedLicenseCount === 0) {
      return ParentRoutingAccess.ClassroomLicensesNotAssigned;
    }
    return null;
  }

  isUnauthorizedRoute(url: string, additionalRoutes: string[] = []): boolean {
    const unAuthorizedRoutes: string[] = [
      '/logout',
      '/login',
      '/login/user',
      '/login/teacher',
      '/login/student',
      '/login/skof',
      '/login/forgot-password',
      '/invalid-token',
      '/set-password',
      '/account-setup',
      '/clever',
      '/lti-token-exchange',
    ].concat(additionalRoutes);
    return unAuthorizedRoutes.includes(url.replace(/\?.*$/, ''));
  }

  /**
   * Teacher cannot access products without 1 Product license.
   * Teacher can access classes without class licenses.
   *
   * @param productLines
   * @param classrooms
   * @param currentClassroom
   *
   * @returns { TeacherLicenseAccess }
   */
  teacherAssignedLicenseAccessCheck(
    productLines: IProductLine[], classrooms: Classroom[], currentClassroom: IClassroomContext
  ): TeacherLicenseAccess {
    const hasClassroomProducts = Helpers.getHasClassroomProducts(productLines);
    const hasTeacherProducts = Helpers.getHasTeacherProducts(productLines);

    if (hasTeacherProducts) {
      return TeacherLicenseAccess.RouteToProducts;
    }

    // Teacher needs licenses assigned if no teacher nor classroom licenses assigned
    // and no current classroom in context.
    if (!hasClassroomProducts && !hasTeacherProducts && !currentClassroom) {
      return TeacherLicenseAccess.NeedsAvailableLicensesAssigned;
    }

    // Teacher can nav to current classroom in context if no teacher nor classroom products
    if (!hasClassroomProducts && !hasTeacherProducts && currentClassroom) {
      return TeacherLicenseAccess.RouteToCurrentClassroom;
    }

    // Teacher can nav to classrooms when no teacher products and has classroom products
    if (hasClassroomProducts) {
      if (Helpers.getHasClassroomProductsWithMaterialPage(productLines)) {
        return TeacherLicenseAccess.RouteToProducts;
      }
      if (!classrooms?.length) {
        return TeacherLicenseAccess.RouteToNewClass;
      }
      // Route to the first classroom if there is 1
      if (classrooms?.[0]?.classroomId) {
        return TeacherLicenseAccess.RouteToFirstClassroom;
      }

      // Route to Create Class page
      return TeacherLicenseAccess.RouteToClassrooms;
    }

    if (classrooms?.length) {
      return TeacherLicenseAccess.RouteToFirstClassroom;
    }

    return null;
  }
}
